import { auth } from '@dashai/lib/firebase';
import { track } from '@/analytics';

/**
 * Track a download event for admin dashboard analytics.
 * Fire-and-forget — errors are logged but never block the UI.
 *
 * Calls `dashaiAdminTrackDownload` on Firebase. The endpoint takes the
 * caller's uid from the Firebase ID token (server-injected).
 *
 * `idClient` tags the event with the caller's current workspace so the
 * admin Live Events panel can show the workspace context the download
 * was generated in.
 */
export async function trackDownload(params: {
  downloadType: 'pptx' | 'pdf' | 'excel' | 'csv' | 'png';
  source?: string;
  fileName?: string;
  idClient?: string;
}) {
  try {
    // Dual-write to the governed behavior_events catalog. Independent
    // of the legacy path's token/URL gating below; export_type is the download
    // format (always present), source is the feature that produced it. row_count
    // isn't available at this shared choke point, so it's omitted.
    track('data_exported', {
      export_type: params.downloadType,
      ...(params.source ? { source: params.source } : {}),
    });

    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) return;

    const fbBase = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
    if (!fbBase) {
      console.error(
        '[trackDownload] VITE_FIREBASE_FUNCTIONS_URL is not set — cannot reach dashaiAdminTrackDownload.',
      );
      return;
    }
    await fetch(`${fbBase.replace(/\/+$/, '')}/dashaiAdminTrackDownload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idClient: params.idClient,
        downloadType: params.downloadType,
        source: params.source,
        fileName: params.fileName,
      }),
    });
  } catch {
    // Silent fail — tracking should never block the user
  }
}
